/** @file stepQuadEdit: one pointer move of a quad drag: the new size or pivot shown through the override. */
import { movePivot } from './movePivot';
import { QUAD_HANDLE_SPOTS } from './quadHandleSpots';
import { resizeEntry } from './resizeEntry';
import { screenToTile } from './screenToTile';
import type { QuadEdit } from './viewportCtx';

/** A handle's pulled edge per screen axis: -1 the min edge, 1 the max edge, 0 none. */
const sideOf = (t: number | undefined): number => (t === 0 ? -1 : t === 1 ? 1 : 0);

/**
 * The pointer at `p` (target px): the travel since the grab, measured along the quad's own screen axes (tile px) and
 * mirrored where the image is, resizes the entry (`resizeEntry`: whole pixels, pivot pixel kept) or moves its pivot
 * (`movePivot`: half pixels). Writes `edit.size`, `edit.pivot` and `moved`; a pending (released) drag is left alone.
 */
export function stepQuadEdit(edit: QuadEdit, p: readonly [number, number]): void {
  if (edit.pending) return;
  const d = screenToTile(edit.u, edit.v, [p[0] - edit.start[0], p[1] - edit.start[1]]);
  const delta: [number, number] = [edit.flip[0] ? -d[0] : d[0], edit.flip[1] ? -d[1] : d[1]];
  edit.moved = true;
  if (edit.handle === 8) {
    edit.size = [edit.size0[0], edit.size0[1]];
    edit.pivot = movePivot(edit.size0, edit.pivot0, delta);
    return;
  }
  const spot = QUAD_HANDLE_SPOTS[edit.handle];
  const sides: [number, number] = [sideOf(spot?.[0]) * (edit.flip[0] ? -1 : 1), sideOf(spot?.[1]) * (edit.flip[1] ? -1 : 1)];
  const r = resizeEntry({ size: edit.size0, pivot: edit.pivot0 }, sides, delta);
  edit.size = r.size;
  edit.pivot = r.pivot;
}
